import { getDate, isEqual } from "date-fns";
import {
  isEventActive,
  isEventFinished,
} from "@circle-react/components/Events/NewEventsSpace/shared/helpers";
import { addToCalendarHelper } from "@circle-react/components/Events/helpers/addToCalendarHelper";
import {
  getLocalizedDateRange,
  getLongLocalizedDateRange,
} from "@circle-react/helpers/dateTimeHelpers/getLocalizedRange";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useEventsFeatureFlags } from "@circle-react/hooks/useEventsFeatureFlags";
import { useSmOrMdScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import type { Event } from "@circle-react/types/Event";
import { AddToCalendar } from "@circle-react-uikit/AddToCalendar";
import { Typography } from "@circle-react-uikit/Typography";

export interface EventTimestampProps {
  event: Event;
  displayAddToCalenderInline?: boolean;
  isRenderedOutsideListItem?: boolean;
  isThumbnailView?: boolean;
}

export const ListViewEventTimestamp = ({
  event,
  displayAddToCalenderInline = true,
  isRenderedOutsideListItem = false,
  isThumbnailView = false,
}: EventTimestampProps) => {
  const { hasSelfUpdateCalendarEnabled } = useEventsFeatureFlags();
  const {
    event_setting_attributes: {
      starts_at: startsAtString,
      ends_at: endsAtString,
      time_zone: timeZone,
      time_zone_abbreviations: timeZoneAbbreviation,
    },
    rsvped_event: hasRsvpedEvent,
  } = event;

  const isSmallScreen = useSmOrMdScreenMediaQuery();

  const startsAtDate = new Date(startsAtString);
  const endsAtDate = new Date(endsAtString);

  const isOneDayEvent = isEqual(getDate(startsAtDate), getDate(endsAtDate));

  if (isRenderedOutsideListItem) {
    const timeStampWithEndTime = getLongLocalizedDateRange(
      startsAtDate,
      endsAtDate,
      timeZone,
    );

    const shouldDisplayInline =
      displayAddToCalenderInline && !isSmallScreen && isOneDayEvent;

    return (
      <div
        className={classNames("flex flex-wrap", {
          "flex-row items-center gap-x-2": shouldDisplayInline,
          "flex-col items-start gap-y-1": !shouldDisplayInline,
        })}
      >
        <Typography.LabelSm weight="semibold" color="text-default">
          {`${timeStampWithEndTime} ${timeZoneAbbreviation}`}
        </Typography.LabelSm>
        {!hasSelfUpdateCalendarEnabled &&
          !isEventFinished(event) &&
          !isEventActive(event) &&
          hasRsvpedEvent && (
            <>
              {shouldDisplayInline && (
                <div className="dark:bg-dark bg-disabled h-1 w-1 rounded-full sm:block" />
              )}
              <div className="text-sm">
                <AddToCalendar {...addToCalendarHelper(event)} />
              </div>
            </>
          )}
      </div>
    );
  }

  const getDateRange = isThumbnailView
    ? getLongLocalizedDateRange
    : getLocalizedDateRange;

  const timeStampWithEndTime = getDateRange(startsAtDate, endsAtDate, timeZone);

  return (
    <Typography.LabelXs as="span">{`${timeStampWithEndTime} ${timeZoneAbbreviation}`}</Typography.LabelXs>
  );
};
